import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import moment from 'moment';
import type React from 'react';
import type { MetaLeadsStats } from 'services/api/metaInsights.api';
import { META_BLUE, NAVY, RADIUS_ITEM, STATUS_NEUTRAL, SURFACE_CONTAINER_LOW, WHATSAPP_GREEN } from 'styles/tokens';

export const labelSx = {
  color: alpha(NAVY, 0.55),
  textTransform: 'uppercase' as const,
  letterSpacing: '0.06em',
  fontSize: '0.7rem',
  fontWeight: 600,
  fontFamily: 'Jost, sans-serif',
};

export const metricValueSx = {
  color: NAVY,
  fontFamily: 'Jost, sans-serif',
  fontWeight: 700,
  fontSize: '1.25rem',
};

export const microLabelSx = {
  fontFamily: 'Jost, sans-serif',
  color: alpha(NAVY, 0.5),
  fontSize: '0.62rem',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
  letterSpacing: '0.04em',
};

export const microValueSx = {
  fontFamily: 'Jost, sans-serif',
  color: NAVY,
  fontSize: '0.78rem',
  fontWeight: 600,
};

export const sectionTitleSx = {
  fontFamily: 'Jost, sans-serif',
  color: NAVY,
  fontWeight: 700,
  fontSize: '0.95rem',
};

type DataSource = 'meta' | 'crm';

const DATA_SOURCE_BADGE: Record<DataSource, { label: string; color: string }> = {
  meta: { label: 'desde Marketing API', color: META_BLUE },
  crm: { label: 'desde nuestra base de datos', color: WHATSAPP_GREEN },
};

/**
 * Section label with an optional badge that tells the user where the numbers
 * below come from: Meta's Marketing API vs. our own CRM database.
 */
export const SectionHeader: React.FC<{ title: string; source?: DataSource }> = ({ title, source }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
    <Typography sx={labelSx}>{title}</Typography>
    {source && (
      <Box
        component="span"
        sx={{
          fontFamily: 'Jost, sans-serif',
          fontSize: '0.6rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: DATA_SOURCE_BADGE[source].color,
          bgcolor: alpha(DATA_SOURCE_BADGE[source].color, 0.12),
          borderRadius: RADIUS_ITEM,
          px: 0.75,
          py: 0.25,
          lineHeight: 1.5,
        }}
      >
        {DATA_SOURCE_BADGE[source].label}
      </Box>
    )}
  </Box>
);

export const formatNumber = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return '—';
  return n.toLocaleString('es-CL', { maximumFractionDigits: 0 });
};

export const formatCurrency = (value: number | string | null | undefined, currency: string | null): string => {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return '—';
  const prefix = currency ? `${currency}$ ` : '$ ';
  const minDecimals = n < 10 ? 2 : 0;
  return `${prefix}${n.toLocaleString('es-CL', { minimumFractionDigits: minDecimals, maximumFractionDigits: minDecimals })}`;
};

export const formatPct = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '—';
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(2)}%`;
};

export const costPerLead = (spend: number | null, count: number | undefined): number | null => {
  if (spend === null || !count || count <= 0) return null;
  return spend / count;
};

export const parseSpend = (value: string | number | undefined | null): number | null => {
  if (value === undefined || value === null || value === '') return null;
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

interface MetricCardProps {
  label: string;
  value: string;
}

export const MetricCard: React.FC<MetricCardProps> = ({ label, value }) => (
  <Box
    sx={{
      p: 2,
      borderRadius: RADIUS_ITEM,
      bgcolor: SURFACE_CONTAINER_LOW,
      display: 'flex',
      flexDirection: 'column',
      gap: 0.5,
    }}
  >
    <Typography sx={labelSx}>{label}</Typography>
    <Typography sx={metricValueSx}>{value}</Typography>
  </Box>
);

interface KpiGridProps {
  insights: {
    spend?: string;
    impressions?: string;
    reach?: string;
    clicks?: string;
    ctr?: string;
    cpc?: string;
  } | null;
  currency: string | null;
}

export const MetaInsightsKpiGrid: React.FC<KpiGridProps> = ({ insights, currency }) => {
  const cards = [
    { label: 'Gasto', value: formatCurrency(insights?.spend ?? null, currency) },
    { label: 'Impresiones', value: formatNumber(insights?.impressions) },
    { label: 'Alcance', value: formatNumber(insights?.reach) },
    { label: 'Clicks', value: formatNumber(insights?.clicks) },
    { label: 'CTR', value: formatPct(insights?.ctr) },
    { label: 'CPC', value: formatCurrency(insights?.cpc ?? null, currency) },
  ].filter((card) => card.value !== '—');

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.5 }}>
      {cards.map((card) => (
        <MetricCard key={card.label} label={card.label} value={card.value} />
      ))}
    </Box>
  );
};

interface LeadStatsStripProps {
  stats: MetaLeadsStats | null;
  spend: number | null;
  currency: string | null;
}

export interface DerivedKpiInputs {
  spend: number | null;
  clicks: number | null;
  contactados: number | null;
  inCrm: number | null;
  currency: string | null;
}

interface DerivedKpi {
  label: string;
  value: string;
  hint?: string;
}

const buildDerivedKpis = (inputs: DerivedKpiInputs): DerivedKpi[] => {
  const { spend, clicks, contactados, inCrm, currency } = inputs;
  const cplContactado = costPerLead(spend, contactados ?? undefined);
  const cplCrm = costPerLead(spend, inCrm ?? undefined);
  const closeRate = contactados && contactados > 0 && inCrm !== null ? inCrm / contactados : null;
  const clickToCrm = clicks && clicks > 0 && inCrm !== null ? inCrm / clicks : null;

  const out: DerivedKpi[] = [
    { label: 'CPL Contactado', value: formatCurrency(cplContactado, currency) },
    { label: 'CPL CRM', value: formatCurrency(cplCrm, currency), hint: 'spend / CRM' },
    {
      label: 'Tasa de cierre',
      value: closeRate !== null ? `${(closeRate * 100).toFixed(1)}%` : '—',
      hint: 'CRM / contactados',
    },
  ];
  if (clickToCrm !== null) {
    out.push({
      label: 'Click → CRM',
      value: `${(clickToCrm * 100).toFixed(2)}%`,
      hint: 'CRM / clicks',
    });
  }
  return out;
};

/**
 * Strip of business KPIs not returned raw by the API: per-stage cost per lead
 * and conversion rates that combine Ads + Leads.
 */
export const DerivedKpis: React.FC<{ inputs: DerivedKpiInputs }> = ({ inputs }) => {
  const kpis = buildDerivedKpis(inputs);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1 }}>
      {kpis.map((k) => (
        <Box
          key={k.label}
          sx={{
            p: 1.5,
            borderRadius: RADIUS_ITEM,
            bgcolor: SURFACE_CONTAINER_LOW,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
          }}
        >
          <Typography sx={microLabelSx}>{k.label}</Typography>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, fontWeight: 700, fontSize: '0.95rem' }}>
            {k.value}
          </Typography>
          {k.hint && (
            <Typography
              sx={{ fontFamily: 'Jost, sans-serif', color: alpha(NAVY, 0.45), fontSize: '0.62rem', fontWeight: 500 }}
            >
              {k.hint}
            </Typography>
          )}
        </Box>
      ))}
    </Box>
  );
};

export interface LeadCountsInputs {
  contactados: number | null;
  inCrm: number | null;
}

/**
 * Absolute lead counts (Contactados, CRM) shown alongside the derived rates
 * so users don't need to back-calculate from percentages.
 */
export const LeadCounts: React.FC<{ inputs: LeadCountsInputs }> = ({ inputs }) => {
  const { contactados, inCrm } = inputs;
  const items: { label: string; value: number | null }[] = [
    { label: 'Contactados', value: contactados },
    { label: 'En CRM', value: inCrm },
  ];
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 1 }}>
      {items.map((it) => (
        <Box
          key={it.label}
          sx={{
            p: 1.5,
            borderRadius: RADIUS_ITEM,
            bgcolor: SURFACE_CONTAINER_LOW,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.25,
          }}
        >
          <Typography sx={microLabelSx}>{it.label}</Typography>
          <Typography sx={{ fontFamily: 'Jost, sans-serif', color: NAVY, fontWeight: 700, fontSize: '0.95rem' }}>
            {formatNumber(it.value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

export const LeadStatsStrip: React.FC<LeadStatsStripProps> = ({ stats, spend, currency }) => {
  if (!stats) {
    return <Typography sx={{ ...microLabelSx, color: STATUS_NEUTRAL }}>Sin datos de leads en este período</Typography>;
  }
  const cplCrm = costPerLead(spend, stats.leadsUploadedToCRM);
  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
      <Box>
        <Typography sx={microLabelSx}>Contactados</Typography>
        <Typography sx={microValueSx}>{formatNumber(stats.leadsWithClientResponses)}</Typography>
      </Box>
      <Box>
        <Typography sx={microLabelSx}>CRM</Typography>
        <Typography sx={microValueSx}>{formatNumber(stats.leadsUploadedToCRM)}</Typography>
      </Box>
      <Box>
        <Typography sx={microLabelSx}>CPL CRM</Typography>
        <Typography sx={microValueSx}>{formatCurrency(cplCrm, currency)}</Typography>
      </Box>
    </Box>
  );
};

type MetaLeadFilterField = 'metaCampaignExternalId' | 'metaAdSetExternalId' | 'metaAdExternalId';

/**
 * Deep-links to the Leads table (list view) pre-filtered by a Meta asset's external ID and,
 * when provided, the same date window as the drill-down so the lead counts line up.
 */
const buildLeadsFilterUrl = (params: {
  businessUnitId?: number;
  field: MetaLeadFilterField;
  value: string;
  since?: string;
  until?: string;
}): string => {
  const { businessUnitId, field, value, since, until } = params;
  const search = new URLSearchParams();
  search.set('viewMode', 'table');
  if (businessUnitId != null) search.set('businessUnitId', String(businessUnitId));
  search.set('leads.advancedFilterModel', JSON.stringify({ items: [{ field, operator: 'contains', value }] }));
  if (since) search.set('leads.dateRange.start', moment(since).startOf('day').toISOString());
  if (until) search.set('leads.dateRange.end', moment(until).endOf('day').toISOString());
  return `/leads?${search.toString()}`;
};

interface ViewLeadsLinkProps {
  field: MetaLeadFilterField;
  value: string | null | undefined;
  label: string;
  businessUnitId?: number;
  since?: string;
  until?: string;
}

export const ViewLeadsLink: React.FC<ViewLeadsLinkProps> = ({ field, value, label, businessUnitId, since, until }) => {
  if (!value) return null;
  return (
    <Button
      component="a"
      href={buildLeadsFilterUrl({ businessUnitId, field, value, since, until })}
      target="_blank"
      rel="noopener noreferrer"
      startIcon={<GroupOutlinedIcon />}
      sx={{
        alignSelf: 'flex-start',
        textTransform: 'none',
        fontFamily: 'Jost, sans-serif',
        fontWeight: 600,
        fontSize: '0.82rem',
        color: META_BLUE,
        bgcolor: alpha(META_BLUE, 0.1),
        borderRadius: RADIUS_ITEM,
        px: 1.75,
        py: 0.75,
        '&:hover': { bgcolor: alpha(META_BLUE, 0.16) },
      }}
    >
      {label}
    </Button>
  );
};
